import campaignsService, { TASK_STATUSES } from '@/api/campaignsService'
import { mapMutations, mapGetters, mapActions, mapState } from 'vuex'
import { GETTERS, MODULE_NAME as MODULE_NAME_FILTERS } from '@/store/filters/constants'
import { CAMPAIGN_STATUSES } from '@/utils/campaignOptions'
import {
  MODULE_NAME as MODULE_NAME_CAMPAIGNS,
  MUTATIONS as MUTATIONS_CAMPAIGNS,
  ACTIONS as ACTIONS_CAMPAIGNS
} from '@/store/campaigns/constants'
import downloadBlob from '@/utils/downloadBlob'
import authService, { USER_ROLES } from '@/api/authService'
import { EXCEL_MIME_TYPE } from '@/utils/csvUtils'

export default {
  name: 'Campaigns',
  mixins: [],
  components: {
    CampaignCreationButton: () => import('@/components/campaign-creation-button/CampaignCreationButton.vue'),
    CampaignCard: () => import('@/components/campaign-card/CampaignCard.vue'),
    DropDown: () => import('@/components/common/drop-down/DropDown.vue'),
    CamapaignCardCompareDropdown: () => import('@/components/camapaign-card-compare-dropdown/CamapaignCardCompareDropdown.vue'),
    CopyAdvancedModal: () => import('@/components/copy-advanced-modal/CopyAdvancedModal.vue')
  },
  props: {},
  data () {
    return {
      authData: authService.authData,
      summaryDownloadLoading: false,
      campaigns: {
        meta: { loading: true },
        results: []
      },
      compareMode: false,
      campaigns_compare: [],
      campaignToCopy: null
    }
  },
  mounted () {
    this.fetchInitialCampaigns()
    this.fetchCopyTasks()
    window.addEventListener('scroll', this.onScroll)
  },
  beforeDestroy () {
    this.clearCopyTasks()
    window.removeEventListener('scroll', this.onScroll)
  },
  computed: {
    ...mapGetters(MODULE_NAME_FILTERS, {
      filters: GETTERS.GET_ROUTE_FILTERS
    }),
    ...mapState(MODULE_NAME_CAMPAIGNS, {
      tasks: 'copy_tasks'
    }),
    numCompletedTasks () {
      return this.tasks.results.filter(t => t.status === TASK_STATUSES.COMPLETED).length
    },
    isExteriorAgency () {
      return this.authData.user.role === USER_ROLES.AGENCY_EXTERIOR
    },
    loading () {
      return this.campaigns.meta.loading
    },
    canShowSummaryDownload () {
      return this.authData.user.role === USER_ROLES.AGENCY ||
        this.authData.user.role === USER_ROLES.ROOT
    },
    isCompareDisabled () {
      return this.campaigns_compare.length < 2
    },
    canCreateCamapign () {
      return this.authData.user.role !== USER_ROLES.AGENCY
    }
  },
  watch: {
    filters () {
      this.fetchInitialCampaigns()
    },
    numCompletedTasks (newValue, oldValue) {
      if (newValue && newValue > oldValue) {
        this.fetchInitialCampaigns()
      }
    }
  },
  methods: {
    ...mapActions(MODULE_NAME_CAMPAIGNS, [ACTIONS_CAMPAIGNS.FETCH_COPY_TASKS]),
    ...mapMutations(MODULE_NAME_CAMPAIGNS, [ MUTATIONS_CAMPAIGNS.SET_META, MUTATIONS_CAMPAIGNS.CLEAR_COPY_TASKS ]),
    fetchInitialCampaigns () {
      this.campaigns.results = []
      this.fetchCampaignsPage(1)
    },
    async fetchCampaignsPage (page) {
      const needsMore = this.campaigns.results.length < this.campaigns.meta.count
      if (page !== 1 && !needsMore) {
        return
      }
      this.$set(this.campaigns.meta, 'loading', true)
      this[MUTATIONS_CAMPAIGNS.SET_META](this.campaigns.meta)
      try {
        const data = await campaignsService.getCampaigns({ page, page_size: 10, ordering: '-created_at' }, this.filters)
        data.results = this.campaigns.results.concat(data.results)
        this.campaigns = data
        this[MUTATIONS_CAMPAIGNS.SET_META](data.meta)
        this.$set(this.campaigns.meta, 'loading', false)
      } catch (err) {
        // Get error text
        const errorText = err.response ? err.response.data.detail : err.message
        // If error occurs and NOT is for cancelled request, set loading to false
        if (!errorText.includes('Canceled')) {
          this.$set(this.campaigns.meta, 'loading', false)
        }
      }

      this[MUTATIONS_CAMPAIGNS.SET_META](this.campaigns.meta)
    },
    fetchCopyTasks () {
      this[ACTIONS_CAMPAIGNS.FETCH_COPY_TASKS]()
    },
    clearCopyTasks () {
      this[MUTATIONS_CAMPAIGNS.CLEAR_COPY_TASKS]()
    },
    onScroll () {
      const el = document.scrollingElement
      const y = el.scrollTop
      const scrollThreshold = 0.9
      const maxY = el.scrollHeight - el.offsetHeight
      if (!this.loading && y > (maxY * scrollThreshold)) {
        const nextPage = this.campaigns.meta.page + 1
        this.fetchCampaignsPage(nextPage)
      }
    },

    addCampaign (campaign) {
      if (campaign) {
        this.campaigns.meta.count++
        this[MUTATIONS_CAMPAIGNS.SET_META](this.campaigns.meta)
        this.campaigns.results.push(campaign)
      }
    },
    editCampaign (campaign) {
      if (campaign) {
        this.campaigns.results = this.campaigns.results
          .map(c => c.id === campaign.id ? campaign : c)
      }
    },
    deleteCampaign (id) {
      this.campaigns.meta.count--
      this[MUTATIONS_CAMPAIGNS.SET_META](this.campaigns.meta)
      this.campaigns.results = this.campaigns.results
        .filter(c => c.id !== id)
    },
    async downloadSummary () {
      this.summaryDownloadLoading = true
      try {
        const data = await campaignsService.getSummaryCSV(this.filters)
        downloadBlob(data, EXCEL_MIME_TYPE, 'campaigns_summary.xlsx')
      } catch (err) {
        // eslint-disable-next-line
        console.error('[campaigns.js] Error downloading campaigns summary', err)
      }
      this.summaryDownloadLoading = false
    },

    goToCompare () {
      this.$router.push(`campaign-compare/${this.campaigns_compare.map(i => i.id).join()}`)
    },
    compare () {
      this.compareMode = true
    },
    closeCompare () {
      this.compareMode = false
      this.campaigns_compare = []
    },
    onCampaignSelect (event) {
      if (!this.isCampaignSelected(event.campaign.id)) {
        this.campaigns_compare.push(event.campaign)
      } else {
        this.campaigns_compare = this.campaigns_compare.filter(c => c.id !== event.campaign.id)
      }
      this.campaigns_compare = [...this.campaigns_compare]
    },
    isCampaignSelected (id) {
      return this.campaigns_compare.some(c => c.id === id)
    },
    isCampaignDisabled (id) {
      const campaignSelectedStatus = this.campaigns.results.filter(c => c.id === id)[0].status
      const validStatus = [CAMPAIGN_STATUSES.ACTIVE, CAMPAIGN_STATUSES.FINISHED].includes(campaignSelectedStatus)
      return (this.campaigns_compare.length >= 3 && !this.isCampaignSelected(id)) || !validStatus
    },
    onCampaignDiscard (campaign) {
      this.campaigns_compare = [...this.campaigns_compare.filter(c => c.id !== campaign.id)]
    },
    copyCampaign ({ id }) {
      this.campaignToCopy = id
    },
    closeCopyModal (refetch = false) {
      this.campaignToCopy = null
      if (refetch) {
        this.fetchInitialCampaigns()
        this.fetchCopyTasks()
      }
    }
  }
}
